import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient, mapMemberToVoiceTeamMember } from './_shared';

// ── Voice settings (/voice-settings) ─────────────────────────────────

export type VoiceSettingsTeamMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar: string;
  lastActive: string;
};

export type VoiceSettingsUsageStat = { label: string; val: string; delta: string };

export type VoiceSettingsPlanUsageRow = {
  label: string;
  used: string;
  total: string;
  pct: number;
};

export type VoiceSettingsPayload = {
  teamMembers: VoiceSettingsTeamMember[];
  usageStats: VoiceSettingsUsageStat[];
  planUsageRows: VoiceSettingsPlanUsageRow[];
  planLabel: string;
};

const INITIAL_VOICE_SETTINGS: VoiceSettingsPayload = {
  teamMembers: [
    { id: '1', name: 'Anil R.', email: 'anil@boostt.ai', role: 'Owner', avatar: 'AR', lastActive: 'Now' },
    { id: '2', name: 'Sam K.', email: 'sam@boostt.ai', role: 'Admin', avatar: 'SK', lastActive: '2h ago' },
    { id: '3', name: 'Maya P.', email: 'maya@boostt.ai', role: 'Editor', avatar: 'MP', lastActive: 'Yesterday' },
    { id: '4', name: 'Dev S.', email: 'dev@boostt.ai', role: 'Viewer', avatar: 'DS', lastActive: '3 days ago' },
  ],
  usageStats: [
    { label: 'Total sessions', val: '2,847', delta: '+312 this month' },
    { label: 'Minutes used', val: '9,142', delta: '67% of plan' },
    { label: 'Booking fees', val: '$4,016', delta: 'MTD spend' },
    { label: 'SMS sent', val: '1,482', delta: '$148.20' },
  ],
  planUsageRows: [
    { label: 'Voice minutes', used: '9,142', total: '15,000', pct: 61 },
    { label: 'Concurrent agents', used: '3', total: '10', pct: 30 },
    { label: 'Knowledge sources', used: '12', total: '50', pct: 24 },
    { label: 'SMS credits', used: '1,482', total: '5,000', pct: 30 },
  ],
  planLabel: 'Scale Plan',
};

let voiceSettingsTeamStore: VoiceSettingsTeamMember[] = [...INITIAL_VOICE_SETTINGS.teamMembers];
let voiceSettingsUsageStore: VoiceSettingsUsageStat[] = [...INITIAL_VOICE_SETTINGS.usageStats];
let voiceSettingsPlanRowsStore: VoiceSettingsPlanUsageRow[] = [...INITIAL_VOICE_SETTINGS.planUsageRows];
let voiceSettingsPlanLabelStore = INITIAL_VOICE_SETTINGS.planLabel;

function mapBillingUsageToVoiceSettings(usage: any): Pick<VoiceSettingsPayload, 'usageStats' | 'planUsageRows' | 'planLabel'> {
  if (!usage || typeof usage !== 'object') {
    return { usageStats: [], planUsageRows: [], planLabel: 'Workspace' };
  }
  const cents = usage.total_usd_cents ?? 0;
  const sessions = usage.session_count ?? 0;
  return {
    usageStats: [
      { label: 'Total sessions', val: String(sessions), delta: '' },
      { label: 'Period spend (USD)', val: `$${(cents / 100).toFixed(2)}`, delta: '' },
      { label: 'Total credits', val: String(usage.total_credits ?? '—'), delta: '' },
    ],
    planUsageRows: [],
    planLabel: 'Workspace usage',
  };
}

export async function fetchVoiceSettings(axiosInstance?: AxiosInstance): Promise<VoiceSettingsPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const membersUrl = `${apiConfig.voiceEndpoints.workspaces}/current/members`;
  const usageUrl = `${apiConfig.voiceEndpoints.billing}/usage`;

  const [membersRes, usageRes] = await Promise.all([
    client.get<any[]>(membersUrl),
    client.get<any>(usageUrl),
  ]);

  const billingSlice = mapBillingUsageToVoiceSettings(usageRes.data);

  return {
    teamMembers: (membersRes.data || []).map(mapMemberToVoiceTeamMember),
    usageStats: billingSlice.usageStats,
    planUsageRows: billingSlice.planUsageRows,
    planLabel: billingSlice.planLabel,
  };
}

export async function saveVoiceGeneralSettings(input: Record<string, unknown> = {}, axiosInstance?: AxiosInstance): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workspaces}/current`;
  const body: Record<string, unknown> = {};
  if (input.name != null) body.name = input.name;
  if (input.timezone != null) body.timezone = input.timezone;
  if (input.default_recording_disclosure != null) body.default_recording_disclosure = input.default_recording_disclosure;
  if (input.default_language != null) body.default_language = input.default_language;
  if (input.default_agent_settings != null) body.default_agent_settings = input.default_agent_settings;
  if (Object.keys(body).length === 0 && Object.keys(input).length > 0) {
    Object.assign(body, input);
  }
  await client.put(url, body);
  return { ok: true };
}

export type WorkspaceComplianceUpdate = {
  // Field names below match routes/workspaces.py's CallingHoursModel/
  // FrequencyCapsModel exactly (start/end, per_24h/per_30d) -- this used to
  // send start_hour/end_hour and max_dials_24h/max_dials_30d, which Pydantic
  // silently drops as unrecognized fields, so this structured sync never
  // actually updated anything on the backend.
  calling_hours?: { start?: string; end?: string; state_auto_tighten?: boolean; [key: string]: unknown } | null;
  state_exclusions?: string[] | null;
  frequency_caps?: { per_24h?: number; per_30d?: number; [key: string]: unknown } | null;
  quiet_periods?: string[] | null;
  consent_evidence_retention_years?: number | null;
  ai_disclosure_template?: string | null;
  recording_disclosure?: Record<string, unknown> | null;
};

/** `GET /api/workspaces/current/compliance` — Fetch workspace compliance defaults as data. */
export async function fetchWorkspaceCompliance(axiosInstance?: AxiosInstance): Promise<Record<string, unknown>> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<Record<string, unknown>>(`${apiConfig.voiceEndpoints.workspaces}/current/compliance`);
  return response.data ?? {};
}

/** `PATCH /api/workspaces/current/compliance` — Update workspace compliance defaults. */
export async function updateWorkspaceCompliance(
  patch: WorkspaceComplianceUpdate,
  axiosInstance?: AxiosInstance
): Promise<Record<string, unknown>> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.patch<Record<string, unknown>>(`${apiConfig.voiceEndpoints.workspaces}/current/compliance`, patch);
  return response.data ?? {};
}

/** `GET /api/workspaces/current/compliance/export` — Export compliance audit log. */
export async function exportWorkspaceCompliance(axiosInstance?: AxiosInstance): Promise<Blob> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get(`${apiConfig.voiceEndpoints.workspaces}/current/compliance/export`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function saveVoiceComplianceSettings(
  input: Record<string, unknown> = {},
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workspaces}/current`;
  await client.put(url, { compliance_flags: input });

  // Also sync structured compliance fields via PATCH /compliance if supported
  try {
    const patchPayload: WorkspaceComplianceUpdate = {};
    if (input.state_exclusions && Array.isArray(input.state_exclusions)) {
      patchPayload.state_exclusions = input.state_exclusions as string[];
    }
    if (input.quiet_periods && Array.isArray(input.quiet_periods)) {
      patchPayload.quiet_periods = input.quiet_periods as string[];
    }
    if (input.ai_disclosure_template != null) {
      patchPayload.ai_disclosure_template = String(input.ai_disclosure_template);
    }
    if (input.consent_evidence_retention != null || input.consent_evidence_retention_years != null) {
      patchPayload.consent_evidence_retention_years = Number(input.consent_evidence_retention_years ?? input.consent_evidence_retention) || 4;
    }
    if (input.tcpa_start_hour != null || input.tcpa_end_hour != null) {
      const pad2 = (h: number) => String(Math.max(0, Math.min(23, h))).padStart(2, '0');
      patchPayload.calling_hours = {
        start: `${pad2(Number(input.tcpa_start_hour ?? 8))}:00`,
        end: `${pad2(Number(input.tcpa_end_hour ?? 21))}:00`,
      };
    }
    if (input.frequency_cap_24h != null || input.frequency_cap_30d != null) {
      // FrequencyCapsModel's real fields are per_24h/per_30d (routes/workspaces.py) --
      // this used to send max_dials_24h/max_dials_30d, which Pydantic silently
      // drops as unrecognized, so this sync never actually updated anything here.
      patchPayload.frequency_caps = {
        per_24h: Number(input.frequency_cap_24h ?? 1),
        per_30d: Number(input.frequency_cap_30d ?? 3),
      };
    }
    if (Object.keys(patchPayload).length > 0) {
      await client.patch(`${apiConfig.voiceEndpoints.workspaces}/current/compliance`, patchPayload).catch(() => null);
    }
  } catch {
    // Non-blocking fallback
  }

  return { ok: true };
}

export type DncUploadResult = {
  total_rows: number;
  valid_numbers: number;
  added: number;
  already_present: number;
  dnc_block_list_size: number;
};

export async function uploadDncList(file: File, axiosInstance?: AxiosInstance): Promise<DncUploadResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const formData = new FormData();
  formData.append('file', file);
  const response = await client.post<DncUploadResult>(
    `${apiConfig.voiceEndpoints.workspaces}/current/dnc-list`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

// ── Workspace extras ──────────────────────────────────────────────────

export type DefaultAgentSettings = {
  auto_record?: boolean;
  memory_enabled?: boolean;
  auto_tag_sentiment?: boolean;
  send_booking_confirmations?: boolean;
};

export type VoiceWorkspace = {
  id: string; name: string; timezone?: string; role?: string;
  compliance_flags?: Record<string, unknown>; voicemail_retention_days?: number;
  default_agent_settings?: DefaultAgentSettings; default_language?: string;
};

/** `GET /api/workspaces/me` — list all workspaces the user belongs to. */
export async function fetchMyWorkspaces(axiosInstance?: AxiosInstance): Promise<VoiceWorkspace[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.workspaces}/me`);
  const list = Array.isArray(response.data) ? response.data : response.data?.workspaces ?? [];
  return list.map((w: any) => ({ id: String(w.id ?? ''), name: String(w.name ?? ''), timezone: w.timezone, role: w.role }));
}

/** `GET /api/workspaces/current` — current workspace details. */
export async function fetchCurrentWorkspace(axiosInstance?: AxiosInstance): Promise<VoiceWorkspace | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.workspaces}/current`);
  const d = response.data;
  if (!d) return null;
  return {
    id: String(d.id ?? ''), name: String(d.name ?? ''), timezone: d.timezone, role: d.role,
    compliance_flags: d.compliance_flags ?? {}, voicemail_retention_days: d.voicemail_retention_days,
    default_agent_settings: d.default_agent_settings ?? {}, default_language: d.default_language,
  };
}

/** `PUT /api/workspaces/current` — BOOSTT-3178 voicemail retention window,
 * kept separate from saveVoiceComplianceSettings() below since it's a real
 * top-level Workspace column, not a compliance_flags key. */
export async function saveVoicemailRetentionDays(days: number, axiosInstance?: AxiosInstance): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workspaces}/current`;
  await client.put(url, { voicemail_retention_days: days });
  return { ok: true };
}

/** `GET /api/workspaces/current/members` or `GET /api/workspaces/members` — list workspace members. */
export async function fetchWorkspaceMembers(axiosInstance?: AxiosInstance): Promise<VoiceSettingsTeamMember[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  try {
    const res = await client.get<any[]>(`${apiConfig.voiceEndpoints.workspaces}/current/members`);
    if (Array.isArray(res.data)) {
      return res.data.map(mapMemberToVoiceTeamMember);
    }
  } catch {
    try {
      const aliasRes = await client.get<any[]>(`${apiConfig.voiceEndpoints.workspaces}/members`);
      if (Array.isArray(aliasRes.data)) {
        return aliasRes.data.map(mapMemberToVoiceTeamMember);
      }
    } catch { /* fallback */ }
  }
  return [];
}

/** `PUT /api/workspaces/current/members/{member_id}/role` — update a member's role. */
export async function updateWorkspaceMemberRole(memberId: string, role: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.put(`${apiConfig.voiceEndpoints.workspaces}/current/members/${memberId}/role`, { role });
  return true;
}

/** `DELETE /api/workspaces/current/members/{member_id}` — remove a member from the workspace. */
export async function removeWorkspaceMember(memberId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.workspaces}/current/members/${memberId}`);
  return response.status === 200 || response.status === 204;
}
